/**
 * Utility to interact with Google Books API
 */

const COMMON_CATEGORY_TRANSLATIONS = new Map<string, string>([
    ['Fiction', 'Ficción'],
    ['Juvenile Fiction', 'Ficción Juvenil'],
    ['Science Fiction', 'Ciencia Ficción'],
    ['Fantasy', 'Fantasía'],
    ['Biography & Autobiography', 'Biografía y Autobiografía'],
    ['History', 'Historia'],
    ['Science', 'Ciencia'],
    ['Philosophy', 'Filosofía'],
    ['Psychology', 'Psicología'],
    ['Religion', 'Religión'],
    ['Social Science', 'Ciencias Sociales'],
    ['Business & Economics', 'Negocios y Economía'],
    ['Computers', 'Informática'],
    ['Art', 'Arte'],
    ['Music', 'Música'],
    ['Cooking', 'Cocina'],
    ['Travel', 'Viajes'],
    ['Health & Fitness', 'Salud y Fitness'],
    ['Self-Help', 'Autoayuda'],
    ['Comics & Graphic Novels', 'Cómics y Novelas Gráficas'],
    ['Literary Criticism', 'Crítica Literaria'],
    ['Education', 'Educación'],
    ['Medical', 'Medicina'],
    ['Law', 'Derecho'],
    ['Political Science', 'Ciencia Política'],
])

interface GoogleBookVolumeInfo {
    description?: string
    authors?: string[]
    categories?: string[]
    pageCount?: number
    publisher?: string | string[]
    publishedDate?: string
}

interface GoogleBookItem {
    volumeInfo: GoogleBookVolumeInfo
}

interface GoogleBookResponse {
    items?: GoogleBookItem[]
}

export interface BookInfo {
    description: string
    authors: string[]
    categories: string[]
    page_count: number
    publisher: string
    publishedKey?: string
}

export const searchBookInfo = async (title: string, author?: string | string[]): Promise<BookInfo | null> => {
    if (!title) return null

    try {
        // Construct query: intitle:TITLE+inauthor:AUTHOR
        let query = `intitle:${encodeURIComponent(title)}`
        if (author) {
            // If author is an array, use first author for search
            const searchAuthor = Array.isArray(author) ? author[0] : author
            if (searchAuthor) {
                query += `+inauthor:${encodeURIComponent(searchAuthor)}`
            }
        }

        const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${query}&maxResults=1`)

        if (!response.ok) {
            throw new Error('Google Books API request failed')
        }

        const data = (await response.json()) as GoogleBookResponse

        if (!data.items || data.items.length === 0) {
            return null
        }

        // Use the first (best) match
        const volumeInfo = data.items[0].volumeInfo

        // Translate categories and keep as array
        const translatedCategories = volumeInfo.categories
            ? volumeInfo.categories.map((cat) => {
                // Try exact match
                const exactMatch = COMMON_CATEGORY_TRANSLATIONS.get(cat)
                if (exactMatch) return exactMatch

                // Try partial replacements
                let translated = cat
                COMMON_CATEGORY_TRANSLATIONS.forEach((esp, eng) => {
                    translated = translated.replace(eng, esp)
                })
                return translated
            })
            : []

        return {
            description: volumeInfo.description || '',
            authors: volumeInfo.authors || [], // Already an array in Google Books API
            categories: translatedCategories, // Return as array, not comma-joined
            page_count: volumeInfo.pageCount || 0,
            publisher: Array.isArray(volumeInfo.publisher)
                ? volumeInfo.publisher.join(', ')
                : volumeInfo.publisher || '',
            // Add extra fields if useful later, but strictly sticking to requirements for now
            publishedKey: volumeInfo.publishedDate, // Just for debugging/logging if needed
        }
    } catch (error) {
        console.warn('Error fetching book info:', error)
        return null
    }
}
